import { Autocomplete, Box, CircularProgress, ListItem, ListItemText, TextField, useTheme } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { WidgetProps } from '@rjsf/utils';
import { useQuery } from '@tanstack/react-query';
import { DataLoaderWorkflowExecutionConfiguration, QueryKey, WorkflowNode, WorkflowNodeType } from 'api/model';
import { getNodeConfig, getNodeColorByGroup } from 'api/workflows-steps/formSchema';
import { getLastCompletedWorkflowExecution, getWorkflow, getWorkflowExecution } from 'api/workflows/requests';
import { useMemo } from 'react';

import { AxiosError } from 'axios';
import PreviewWorkflow from 'components/workflows/PreviewWorkflow';
import WorkflowExecutionPreviewDataButton from 'components/workflows/WorkflowExecutionPreviewDataButton';
import { useParams } from 'react-router';
import 'reactflow/dist/style.css';
import { JumpToWorkflow } from 'components/button/JumpToWorkflow';
import { nodeIconStyle } from './ExecutionNodeSelectWidget.styles';

type Row = {
  step: string;
  node_id: string;
  type: WorkflowNodeType;
};

export default function ExecutionNodeSelectWidget(props: WidgetProps<string>) {
  const { value, onChange, required, disabled, formContext, options } = props;
  const { projectId } = useParams();
  const theme = useTheme();

  const configuration = formContext?.formData?.configuration as DataLoaderWorkflowExecutionConfiguration;
  const isTooltipView = formContext?.isTooltipView as boolean;
  const manualExecutionId: string | undefined = configuration?.execution_id;
  const workflowId: string | undefined = configuration?.workflow_id;

  const enabled = Boolean(workflowId);
  const enabledWorkflowExecution = Boolean(manualExecutionId);
  const lastExecutionQueryKey = [QueryKey.WorkflowExecutions, 'last-completed', workflowId];
  const executionsQueryKey = [QueryKey.WorkflowExecutions, manualExecutionId];

  const { data: lastCompletedExec } = useQuery({
    queryKey: lastExecutionQueryKey,
    queryFn: () => getLastCompletedWorkflowExecution(projectId ?? '', workflowId ?? ''),
    enabled: enabled && !enabledWorkflowExecution,
    staleTime: 30_000,
  });

  const { data: execManual } = useQuery({
    queryKey: executionsQueryKey,
    queryFn: () => getWorkflowExecution(manualExecutionId!),
    enabled: enabled && enabledWorkflowExecution,
    staleTime: 30_000,
  });

  const workflow_revision = manualExecutionId ? (execManual?.workflow_revision ?? null) : null;
  const executionId: string | null = manualExecutionId ? manualExecutionId : (lastCompletedExec?.id ?? null);
  const revisionQueryKey = [QueryKey.RevisedWorkflows, { workflowId, workflow_revision }];

  const {
    data: workflow,
    isLoading: isWorkflowLoading,
    isFetching: isWorkflowFetching,
    isError: isWorkflowError,
    error: workflowError,
  } = useQuery({
    queryKey: revisionQueryKey,
    queryFn: () => getWorkflow(workflowId!, workflow_revision),
    enabled,
    staleTime: 30_000,
  });

  // build table options from LAST node only
  const optionsList: Row[] = useMemo(() => {
    if (!workflow?.nodes.length) return [];
    return workflow?.nodes
      .filter((node: WorkflowNode) => {
        if (!node.enabled) {
          return false;
        }
        const nodeConfig = getNodeConfig(node.type);
        if (!nodeConfig) {
          return false;
        }
        return true;
      })
      .map((node: WorkflowNode) => {
        return {
          step: node.name,
          type: node.type as WorkflowNodeType,
          node_id: node.id,
        };
      });
  }, [workflow]);

  const selected = optionsList.find((r: Row) => r.node_id === value) ?? null;

  const filterOptions = useMemo(
    () =>
      createFilterOptions<Row>({
        matchFrom: 'any',
        trim: true,
        stringify: (opt: Row) => {
          const p: string[] = [opt.step];
          return p.join(' ');
        },
      }),
    [],
  );

  const label: string = (options as { label?: string })?.label ?? 'Node';

  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete
        disablePortal
        options={optionsList}
        filterOptions={filterOptions}
        getOptionLabel={(opt) => opt?.step ?? ''}
        value={selected}
        onChange={(_, opt) => onChange(opt?.node_id ?? '')}
        loading={isWorkflowLoading || isWorkflowFetching}
        disabled={disabled || !enabled || !optionsList.length}
        renderInput={(params) => {
          const nodeConfig = selected ? getNodeConfig(selected.type) : null;
          const isError404 = isWorkflowError && (workflowError as AxiosError)?.response?.status === 404;

          return (
            <TextField
              {...params}
              label={label}
              required={required}
              placeholder={
                !enabled
                  ? 'Select workflow first'
                  : isWorkflowLoading
                    ? 'Loading workflow…'
                    : isError404
                      ? 'Workflow not found (404)'
                      : optionsList.length
                        ? 'Search by node name…'
                        : 'No nodes in last node'
              }
              error={isWorkflowError}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {isWorkflowLoading && <CircularProgress color="inherit" size={16} sx={{ ml: 1, mr: 1 }} />}
                    {selected && workflow && nodeConfig && !isWorkflowLoading && !isWorkflowError && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          pl: 1,
                          pr: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            ...nodeIconStyle,
                            backgroundColor: getNodeColorByGroup(nodeConfig.group),
                            color: theme.palette.getContrastText(getNodeColorByGroup(nodeConfig.group)),
                          }}
                        >
                          {nodeConfig.icon}
                        </div>
                      </Box>
                    )}
                    {isWorkflowError && (
                      <Box className="text-red-600 text-sm pl-4 whitespace-nowrap overflow-hidden text-ellipsis">
                        Error loading workflow
                      </Box>
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          );
        }}
        renderOption={(propsMUI, opt) => {
          const nodeConfig = getNodeConfig(opt.type);

          return (
            <ListItem {...propsMUI} key={opt.node_id} dense sx={{ gap: 1 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {nodeConfig && (
                            <div
                              style={{
                                ...nodeIconStyle,
                                backgroundColor: getNodeColorByGroup(nodeConfig.group),
                                color: theme.palette.getContrastText(getNodeColorByGroup(nodeConfig.group)),
                              }}
                            >
                              {nodeConfig.icon}
                            </div>
                          )}
                          <span style={{ fontWeight: 600 }}>{opt.step}</span>
                        </div>
                      </div>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          );
        }}
        renderTags={() => null}
        isOptionEqualToValue={(a, b) => a.node_id === b.node_id}
      />

      <div className="mt-4">
        {enabled && !isTooltipView && (
          <PreviewWorkflow
            workflow={workflow}
            selected={selected?.node_id ?? ''}
            onChange={(node_id: string) => onChange(node_id)}
          />
        )}
      </div>

      <div className="mt-2 flex justify-between">
        {workflow && <JumpToWorkflow projectId={workflow.project_id} workflowId={workflow.id} />}
        {selected?.node_id && executionId && !isTooltipView && (
          <WorkflowExecutionPreviewDataButton
            executionId={executionId}
            nodeId={selected?.node_id ?? ''}
            previewSize={100}
          />
        )}
      </div>
    </Box>
  );
}
